import React, { useState, useEffect } from 'react';
import { 
  FiEdit2, FiTrash2, FiPlus, FiX, FiCheck, FiAlertCircle, FiDownload 
} from 'react-icons/fi';
import { 
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { useNotifications } from '../../context/NotificationContext';
import './AccountingAdjustments.css';

const AccountingAdjustments = () => {
  const { addNotification } = useNotifications();
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    type: 'correction', // correction, adjustment, refund, etc.
    reason: '',
    relatedOrderId: '',
    status: 'pending', // pending, approved, completed
    notes: ''
  });

  useEffect(() => {
    fetchAdjustments();
  }, []);

  const fetchAdjustments = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'accounting_adjustments'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(doc.data().updatedAt)
      }));
      
      setAdjustments(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (error) {
      console.error('Error fetching adjustments:', error);
      addNotification('Failed to load accounting adjustments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description || !formData.amount || !formData.reason) {
      addNotification('Please fill in all required fields', 'error');
      return;
    }

    try {
      if (editingAdjustment) {
        // Update existing adjustment
        const adjustmentRef = doc(db, 'accounting_adjustments', editingAdjustment.id);
        await updateDoc(adjustmentRef, {
          ...formData,
          amount: parseFloat(formData.amount),
          updatedAt: serverTimestamp()
        });
        addNotification('Accounting adjustment updated successfully', 'success');
      } else {
        // Create new adjustment
        await addDoc(collection(db, 'accounting_adjustments'), {
          ...formData,
          amount: parseFloat(formData.amount),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        addNotification('Accounting adjustment created successfully', 'success');
      }

      // Reset form
      setFormData({
        description: '',
        amount: '',
        type: 'correction',
        reason: '',
        relatedOrderId: '',
        status: 'pending',
        notes: ''
      });
      setEditingAdjustment(null);
      setShowForm(false);
      fetchAdjustments();
    } catch (error) {
      console.error('Error saving adjustment:', error);
      addNotification('Failed to save adjustment', 'error');
    }
  };

  const handleEdit = (adjustment) => {
    setEditingAdjustment(adjustment);
    setFormData({
      description: adjustment.description,
      amount: adjustment.amount.toString(),
      type: adjustment.type,
      reason: adjustment.reason,
      relatedOrderId: adjustment.relatedOrderId || '',
      status: adjustment.status,
      notes: adjustment.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this adjustment?')) return;

    try {
      await deleteDoc(doc(db, 'accounting_adjustments', id));
      addNotification('Adjustment deleted successfully', 'success');
      fetchAdjustments();
    } catch (error) {
      console.error('Error deleting adjustment:', error);
      addNotification('Failed to delete adjustment', 'error');
    }
  };

  const handleApprove = async (adjustment) => {
    try {
      const adjustmentRef = doc(db, 'accounting_adjustments', adjustment.id);
      await updateDoc(adjustmentRef, {
        status: 'approved',
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      addNotification('Adjustment approved successfully', 'success');
      fetchAdjustments();
    } catch (error) {
      console.error('Error approving adjustment:', error);
      addNotification('Failed to approve adjustment', 'error');
    }
  };

  // Filter adjustments
  let filteredAdjustments = adjustments;

  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    filteredAdjustments = filteredAdjustments.filter(adj =>
      adj.description.toLowerCase().includes(searchLower) ||
      adj.reason.toLowerCase().includes(searchLower) ||
      adj.relatedOrderId?.toLowerCase().includes(searchLower)
    );
  }

  if (filterStatus !== 'all') {
    filteredAdjustments = filteredAdjustments.filter(adj => adj.status === filterStatus);
  }

  const totalAmount = filteredAdjustments.reduce((sum, adj) => sum + parseFloat(adj.amount), 0);

  if (loading) {
    return <div className="accounting-adjustments loading">Loading accounting adjustments...</div>;
  }

  return (
    <div className="accounting-adjustments">
      <div className="adjustments-header">
        <div>
          <h2>📊 Accounting Adjustments</h2>
          <p>Manage and correct accounting entries to remove errors</p>
        </div>
        <button 
          className="btn-add-adjustment"
          onClick={() => {
            setShowForm(true);
            setEditingAdjustment(null);
            setFormData({
              description: '',
              amount: '',
              type: 'correction',
              reason: '',
              relatedOrderId: '',
              status: 'pending',
              notes: ''
            });
          }}
        >
          <FiPlus /> Add Adjustment
        </button>
      </div>

      {/* Summary Cards */}
      <div className="adjustments-summary">
        <div className="summary-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <p className="card-label">Total Adjustments</p>
            <p className="card-value">KES {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">⏳</div>
          <div className="card-content">
            <p className="card-label">Pending Review</p>
            <p className="card-value">{adjustments.filter(a => a.status === 'pending').length}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">✅</div>
          <div className="card-content">
            <p className="card-label">Approved</p>
            <p className="card-value">{adjustments.filter(a => a.status === 'approved').length}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">🔄</div>
          <div className="card-content">
            <p className="card-label">Completed</p>
            <p className="card-value">{adjustments.filter(a => a.status === 'completed').length}</p>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="adjustment-form-modal">
          <div className="form-overlay" onClick={() => setShowForm(false)}></div>
          <div className="form-container">
            <div className="form-header">
              <h3>{editingAdjustment ? 'Edit Adjustment' : 'Add New Adjustment'}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  setEditingAdjustment(null);
                }}
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="adjustment-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="description">Description *</label>
                  <input
                    id="description"
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="e.g., Tax adjustment, refund correction"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="amount">Amount (KES) *</label>
                  <input
                    id="amount"
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="type">Adjustment Type</label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                  >
                    <option value="correction">Correction</option>
                    <option value="adjustment">Adjustment</option>
                    <option value="refund">Refund</option>
                    <option value="discount">Discount</option>
                    <option value="tax">Tax Adjustment</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason for Adjustment *</label>
                <input
                  id="reason"
                  type="text"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Why is this adjustment needed?"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="relatedOrderId">Related Order ID (Optional)</label>
                <input
                  id="relatedOrderId"
                  type="text"
                  name="relatedOrderId"
                  value={formData.relatedOrderId}
                  onChange={handleInputChange}
                  placeholder="Link to order if applicable"
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Additional Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional information..."
                  rows="3"
                ></textarea>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => {
                    setShowForm(false);
                    setEditingAdjustment(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  <FiCheck /> {editingAdjustment ? 'Update' : 'Create'} Adjustment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="adjustments-filters">
        <div className="filter-search">
          <input
            type="text"
            placeholder="Search by description, reason, or order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-status">
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Adjustments Table */}
      <div className="adjustments-table-container">
        {filteredAdjustments.length === 0 ? (
          <div className="no-adjustments">
            <FiAlertCircle />
            <p>No accounting adjustments found</p>
          </div>
        ) : (
          <table className="adjustments-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Amount (KES)</th>
                <th>Type</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdjustments.map(adjustment => (
                <tr key={adjustment.id} className={`status-${adjustment.status}`}>
                  <td className="col-description">
                    <div className="description-cell">
                      <p className="title">{adjustment.description}</p>
                      {adjustment.relatedOrderId && (
                        <p className="order-id">Order: {adjustment.relatedOrderId}</p>
                      )}
                    </div>
                  </td>
                  <td className="col-amount">
                    <span className="amount">KES {parseFloat(adjustment.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </td>
                  <td className="col-type">
                    <span className={`type-badge ${adjustment.type}`}>
                      {adjustment.type.charAt(0).toUpperCase() + adjustment.type.slice(1)}
                    </span>
                  </td>
                  <td className="col-reason">
                    <p title={adjustment.reason}>{adjustment.reason}</p>
                  </td>
                  <td className="col-status">
                    <span className={`status-badge status-${adjustment.status}`}>
                      {adjustment.status.charAt(0).toUpperCase() + adjustment.status.slice(1)}
                    </span>
                  </td>
                  <td className="col-date">
                    {new Date(adjustment.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td className="col-actions">
                    <div className="action-buttons">
                      {adjustment.status === 'pending' && (
                        <button
                          className="btn-action btn-approve"
                          onClick={() => handleApprove(adjustment)}
                          title="Approve adjustment"
                        >
                          <FiCheck />
                        </button>
                      )}
                      <button
                        className="btn-action btn-edit"
                        onClick={() => handleEdit(adjustment)}
                        title="Edit adjustment"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="btn-action btn-delete"
                        onClick={() => handleDelete(adjustment.id)}
                        title="Delete adjustment"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AccountingAdjustments;
